import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ingestionService from '../services/ingestionService';
import { useDialog } from '../context/DialogContext';
import { useUser } from '../context/UserContext';
import { Btn, CheckBox, Chip, EmptyLine, Field, Loading, StatTile, inputClass, useAdminShell } from './admin/shell';

const VIEWS = ['sources', 'queue', 'claims'];
const ADAPTERS = ['jsonld-render', 'jsonld', 'ics', 'wp-tribe', 'llm-extract', 'zyte', 'dice', 'blogto', 'eventbrite', 'ticketmaster-web',
  'manual-csv', 'castanet', 'chimera', 'chimera-events', 'nownetwork', 'ticketmaster'];

/**
 * ENGINES › INGESTION — spec/sections.md §20. External event ingestion control plane.
 *
 * Sources are internal: nothing here is ever surfaced to guests, and ingested events
 * appear on the wall as ordinary Portal listings.
 *
 * Gates (`ingestion` · view create edit delete publish), absent not disabled:
 *   create  — + ADD SOURCE, SEED SOURCES, CSV staging
 *   edit    — RUN ALL, row DRY / RUN / ENABLE / DISABLE, claim REJECT
 *   publish — PUBLISH DIRECTLY, the MODE review↔auto toggle, queue APPROVE ALL / APPROVE / REJECT, claim APPROVE
 *   delete  — DELETE ALL SCRAPED
 * The sub-view lives in the URL (`/admin/ingestion/queue`) so TODAY can land on it.
 */
export default function IngestionPanel() {
  const { showAlert, showConfirm } = useDialog();
  const { can } = useUser();
  const { refreshCounts } = useAdminShell();
  const navigate = useNavigate();
  const { view: routeView } = useParams();
  const view = VIEWS.includes(routeView) ? routeView : 'sources';
  const canCreate = can('ingestion', 'create');
  const canEdit = can('ingestion', 'edit');
  const canPublish = can('ingestion', 'publish');
  const canDelete = can('ingestion', 'delete');

  const [sources, setSources] = useState([]);
  const [health, setHealth] = useState(null);
  const [showAllFailing, setShowAllFailing] = useState(false);
  const [queue, setQueue] = useState([]);
  const [claims, setClaims] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [csvTarget, setCsvTarget] = useState(null);
  const [csvText, setCsvText] = useState('');
  const [runningAll, setRunningAll] = useState(false);
  const [publishPref, setPublishPref] = useState(true);
  // Never publish-direct without the verb, whatever the preference says.
  const publishAll = canPublish && publishPref;
  const [progress, setProgress] = useState([]); // per-source live status
  const [newSrc, setNewSrc] = useState(null);   // add-source form (null = closed)
  const [newSrcErrors, setNewSrcErrors] = useState({});

  const setView = (v) => navigate(v === 'sources' ? '/admin/ingestion' : `/admin/ingestion/${v}`);

  const load = useCallback(async () => {
    setLoading(true);
    const [s, h] = await Promise.all([ingestionService.getSources(), ingestionService.getHealth()]);
    if (s.success) setSources(s.data);
    if (h.success) setHealth(h.data);
    setLoading(false);
  }, []);

  const loadQueue = useCallback(async () => {
    const r = await ingestionService.getQueue('new', 100);
    if (r.success) setQueue(r.data);
  }, []);

  useEffect(() => { load(); }, [load]);
  const loadClaims = useCallback(async () => {
    const r = await ingestionService.getPendingClaims();
    if (r.success) setClaims(r.data);
  }, []);

  useEffect(() => { if (view === 'queue') loadQueue(); }, [view, loadQueue]);
  useEffect(() => { if (view === 'claims') loadClaims(); }, [view, loadClaims]);

  const handleSeed = async () => {
    const ok = await showConfirm({
      title: 'SEED SOURCES',
      message: 'Insert the verified source list and create the Portal house account. '
        + 'Existing sources are left untouched.',
      confirmLabel: 'SEED',
      variant: 'primary',
    });
    if (!ok) return;
    const r = await ingestionService.seedSources();
    if (r.success) {
      await showAlert({ title: 'SEEDED', message: `Added ${r.data.count} source(s).`, type: 'success' });
      load();
    } else {
      await showAlert({ title: 'FAILED', message: r.error || 'Could not seed sources.', type: 'error' });
    }
  };

  const handleAddSource = async () => {
    const f = newSrc;
    const errors = {};
    if (!f.slug.trim()) errors.slug = 'SLUG IS REQUIRED.';
    if (!f.homepageUrl.trim()) errors.homepageUrl = 'LISTING URL IS REQUIRED.';
    setNewSrcErrors(errors);
    if (Object.keys(errors).length > 0) return;
    const configJson = f.configJson.trim()
      || JSON.stringify({ listingUrls: [f.homepageUrl.trim()], max: 200 });
    const r = await ingestionService.createSource({
      slug: f.slug.trim(), name: f.name.trim() || f.slug.trim(), homepageUrl: f.homepageUrl.trim(),
      adapterKey: f.adapterKey, configJson, publishMode: f.publishMode,
      isEnabled: false, termsStatus: 'unreviewed', crawlIntervalMinutes: 720,
    });
    if (r.success) {
      setNewSrc(null);
      await showAlert({ title: 'ADDED', message: 'Source created (disabled). DRY-run it, then ENABLE.', type: 'success' });
      load();
    } else {
      setNewSrcErrors({ server: r.error || 'COULD NOT CREATE THE SOURCE.' });
    }
  };

  const runReport = (data) =>
    `fetched ${data.fetched} · new ${data.new} · updated ${data.updated} · published ${data.published} · `
    + `queued ${data.queued} · delisted ${data.delisted} · skipped ${data.skipped}\n`
    + `complete: ${data.complete ? 'yes' : 'NO — delist reaper suppressed'}`
    + (data.warning ? `\nwarning: ${data.warning}` : '');

  const handleRun = async (source, dry) => {
    setBusyId(source.id);
    const r = dry ? await ingestionService.dryRun(source.id) : await ingestionService.runNow(source.id);
    setBusyId(null);
    if (r.success) {
      await showAlert({
        title: dry ? `DRY RUN — ${source.slug}` : `RUN — ${source.slug}`,
        message: runReport(r.data),
        type: 'success',
      });
      if (!dry) { load(); refreshCounts(); }
    } else {
      await showAlert({ title: 'RUN FAILED', message: r.error || 'Unknown error', type: 'error' });
    }
  };

  // Runs every source sequentially, updating a live progress row after each so
  // it's clear what's happening (the old flow was one blocking alert per source).
  const handleRunAll = async () => {
    const list = sources;
    if (list.length === 0) return;
    const ok = await showConfirm({
      title: 'RUN ALL SOURCES',
      message: publishAll
        ? `Crawl all ${list.length} sources and PUBLISH mapped events straight to the wall (bypassing the review queue). This can create a lot of events.`
        : `Crawl all ${list.length} sources into the review queue (no events published — approve them in the QUEUE tab).`,
      confirmLabel: 'RUN ALL',
      variant: publishAll ? 'danger' : 'primary',
    });
    if (!ok) return;

    setRunningAll(true);
    setProgress(list.map((s) => ({ id: s.id, slug: s.slug, status: 'pending' })));
    let fetched = 0, published = 0, queued = 0, errors = 0;
    for (const s of list) {
      setProgress((prev) => prev.map((p) => (p.id === s.id ? { ...p, status: 'running' } : p)));
      // eslint-disable-next-line no-await-in-loop
      const r = await ingestionService.runNow(s.id, { publish: publishAll });
      if (r.success) {
        fetched += r.data.fetched || 0; published += r.data.published || 0; queued += r.data.queued || 0;
        setProgress((prev) => prev.map((p) => (p.id === s.id ? { ...p, status: 'done', ...r.data } : p)));
      } else {
        errors += 1;
        setProgress((prev) => prev.map((p) => (p.id === s.id ? { ...p, status: 'error', error: r.error } : p)));
      }
    }
    setRunningAll(false);
    load();
    refreshCounts();
    await showAlert({
      title: 'RUN ALL COMPLETE',
      message: `${list.length} sources · fetched ${fetched} · published ${published} · queued ${queued} · errors ${errors}`
        + (published === 0 && queued > 0 ? '\n\nEverything went to the review QUEUE — turn on "PUBLISH DIRECTLY" (or approve items in the QUEUE tab) to get events onto the wall.' : ''),
      type: errors ? 'warning' : 'success',
    });
  };

  const [purging, setPurging] = useState(false);
  const handlePurge = async () => {
    const ok = await showConfirm({
      title: 'DELETE ALL SCRAPED EVENTS',
      message: 'Remove every scraped event from the wall and clear the staging queue so scrapers can start fresh. '
        + 'This only deletes events that came from the scrapers — user-created events are untouched.',
      confirmLabel: 'DELETE ALL SCRAPED',
      variant: 'danger',
    });
    if (!ok) return;
    setPurging(true);
    const r = await ingestionService.purge();
    setPurging(false);
    if (r.success) {
      setProgress([]);
      await showAlert({ title: 'PURGED', message: `Deleted ${r.data.events} scraped event(s) and cleared ${r.data.items} staged item(s).`, type: 'success' });
      load();
      refreshCounts();
    } else {
      await showAlert({ title: 'FAILED', message: r.error || 'Could not purge.', type: 'error' });
    }
  };

  const handleToggle = async (source) => {
    const r = await ingestionService.updateSource(source.id, { isEnabled: !source.isEnabled });
    if (r.success) load();
    else await showAlert({ title: 'FAILED', message: r.error || 'Could not update.', type: 'error' });
  };

  const handlePublishMode = async (source) => {
    const next = source.publishMode === 'auto' ? 'review' : 'auto';
    if (next === 'auto') {
      const ok = await showConfirm({
        title: 'ENABLE AUTO-PUBLISH',
        message: `"${source.slug}" will publish straight to the wall with no human review. `
          + 'Only do this once its duplicate rate has been observed at zero.',
        confirmLabel: 'ENABLE AUTO',
        variant: 'danger',
      });
      if (!ok) return;
    }
    const r = await ingestionService.updateSource(source.id, { publishMode: next });
    if (r.success) load();
  };

  const handleCsvUpload = async () => {
    if (!csvTarget) return;
    const r = await ingestionService.uploadCsv(csvTarget.id, csvText);
    if (r.success) {
      await showAlert({ title: 'CSV STAGED', message: `Parsed ${r.data.parsedRows} row(s).`, type: 'success' });
      setCsvTarget(null); setCsvText(''); load(); refreshCounts();
    } else {
      await showAlert({ title: 'CSV REJECTED', message: r.error || 'Parse failed.', type: 'error' });
    }
  };

  const handleReview = async (item, action) => {
    const r = await ingestionService.review(item.id, action);
    if (r.success) { loadQueue(); refreshCounts(); }
  };

  const [approvingAll, setApprovingAll] = useState(false);
  const handleApproveAll = async () => {
    const ok = await showConfirm({
      title: 'APPROVE ALL',
      message: `Publish every queued item to the wall now (${queue.length} in view; the run covers all queued items). This creates the events immediately.`,
      confirmLabel: 'APPROVE ALL',
      variant: 'danger',
    });
    if (!ok) return;
    setApprovingAll(true);
    const r = await ingestionService.approveAll();
    setApprovingAll(false);
    if (r.success) {
      await showAlert({ title: 'APPROVED', message: `Published ${r.data.published} · skipped ${r.data.skipped}.`, type: 'success' });
      loadQueue(); load(); refreshCounts();
    } else {
      await showAlert({ title: 'FAILED', message: r.error || 'Could not approve.', type: 'error' });
    }
  };

  const handleClaim = async (claim, action) => {
    if (action === 'approve') {
      const ok = await showConfirm({
        title: 'APPROVE CLAIM',
        message: `Hand "${claim.eventTitle}" to ${claim.requesterName} (${claim.requesterEmail}). `
          + 'They become the host and the event is permanently detached from ingestion — '
          + 'we will stop updating it. Verify they actually run this event first.',
        confirmLabel: 'APPROVE',
        variant: 'danger',
      });
      if (!ok) return;
    }
    const r = await ingestionService.resolveClaim(claim.eventId, action);
    if (r.success) { loadClaims(); refreshCounts(); }
    else await showAlert({ title: 'FAILED', message: r.error || 'Could not resolve claim.', type: 'error' });
  };

  if (loading) return <Loading className="py-12 text-center" />;

  const showControls = canEdit || canCreate || canDelete || canPublish;

  return (
    <div className="flex flex-col gap-4 font-mono">
      {/* ── Health tiles ── */}
      {health && (
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}>
          <StatTile small label="SOURCES" value={health.sourceCount} />
          <StatTile small label="CRAWLABLE" value={health.crawlable} />
          <StatTile small label="INGESTED EVENTS" value={health.ingestedEvents} />
          <StatTile small label="FAILING" value={health.failing?.length ?? 0} />
        </div>
      )}

      {health?.failing?.length > 0 && (
        <div className="border-2 border-black bg-[#F9B82C] px-[14px] py-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div className="text-[11px] font-bold uppercase tracking-[0.1em]">{health.failing.length} SOURCES FAILING</div>
            {health.failing.length > 6 && (
              <button type="button" onClick={() => setShowAllFailing((v) => !v)} className="text-[10px] font-bold uppercase tracking-[0.1em] underline">
                {showAllFailing ? 'SHOW FEWER' : `SHOW ALL ${health.failing.length}`}
              </button>
            )}
          </div>
          <div className="mt-1 text-[11px] leading-[1.6] text-[#1A1A1A]">
            {(showAllFailing ? health.failing : health.failing.slice(0, 6)).map((f, i) => (
              <span key={f.slug}>{i > 0 ? ' · ' : ''}{f.slug} — {f.consecutiveFailures} consecutive{f.lastError ? ` (${String(f.lastError).slice(0, 60)})` : ''}</span>
            ))}
            {!showAllFailing && health.failing.length > 6 && <span> · +{health.failing.length - 6} more</span>}
          </div>
        </div>
      )}

      {/* ── Control row ── */}
      <div className="flex flex-wrap items-center gap-2">
        {VIEWS.map((v) => (
          <Chip key={v} selected={view === v} onClick={() => setView(v)} className="!tracking-[0.1em]">{v.toUpperCase()}</Chip>
        ))}
        {showControls && (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {canPublish && (
              <CheckBox checked={publishAll} onChange={setPublishPref} label="PUBLISH DIRECTLY" />
            )}
            {canEdit && (
              <Btn variant="solid" onClick={handleRunAll} disabled={runningAll}>{runningAll ? 'RUNNING…' : 'RUN ALL'}</Btn>
            )}
            {canCreate && <Btn onClick={handleSeed}>SEED SOURCES</Btn>}
            {canDelete && (
              <Btn variant="danger" onClick={handlePurge} disabled={purging}>{purging ? 'DELETING…' : 'DELETE ALL SCRAPED'}</Btn>
            )}
          </div>
        )}
      </div>

      {/* ── Live run-all progress ── */}
      {progress.length > 0 && (
        <div className="border-2 border-black bg-white">
          <div className="flex justify-between bg-black px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-white">
            <span>{runningAll ? 'RUNNING SOURCES…' : 'LAST RUN'}</span>
            <span>{progress.filter((p) => p.status === 'done' || p.status === 'error').length}/{progress.length}</span>
          </div>
          <div className="max-h-72 divide-y divide-gray-200 overflow-y-auto">
            {progress.map((p) => (
              <div key={p.id} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                <span className={`w-4 font-bold ${p.status === 'error' ? 'text-[#DC2626]' : p.status === 'done' ? 'text-[#15803D]' : 'text-gray-400'}`}>
                  {p.status === 'running' ? '…' : p.status === 'done' ? '✓' : p.status === 'error' ? '✗' : '·'}
                </span>
                <span className="flex-1 truncate font-bold">{p.slug}</span>
                {p.status === 'done' && (
                  <span className="whitespace-nowrap text-gray-600">
                    pub {p.published} · q {p.queued} · fetch {p.fetched}{p.warning ? ' · WARNING' : ''}
                  </span>
                )}
                {p.status === 'error' && <span className="max-w-[50%] truncate text-[#DC2626]">{p.error}</span>}
                {p.status === 'running' && <span className="text-gray-500">running…</span>}
                {p.status === 'pending' && <span className="text-gray-400">queued</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Sources ── */}
      {view === 'sources' && (
        <>
          {canCreate && (
            !newSrc ? (
              <div>
                <Btn variant="solid" size="card" onClick={() => { setNewSrcErrors({}); setNewSrc({ slug: '', name: '', homepageUrl: '', adapterKey: 'jsonld-render', configJson: '', publishMode: 'review' }); }}>
                  + ADD SOURCE
                </Btn>
              </div>
            ) : (
              <div className="flex flex-col gap-3 border-2 border-black bg-white p-[14px]">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Field label="SLUG" error={newSrcErrors.slug}>
                    <input placeholder="vogue-vancouver" value={newSrc.slug}
                      onChange={(e) => { setNewSrc({ ...newSrc, slug: e.target.value }); setNewSrcErrors((x) => ({ ...x, slug: undefined })); }} className={inputClass} />
                  </Field>
                  <Field label="NAME">
                    <input placeholder="Vogue Theatre" value={newSrc.name}
                      onChange={(e) => setNewSrc({ ...newSrc, name: e.target.value })} className={inputClass} />
                  </Field>
                </div>
                <Field label="LISTING URL" error={newSrcErrors.homepageUrl}>
                  <input placeholder="https://…/events" value={newSrc.homepageUrl}
                    onChange={(e) => { setNewSrc({ ...newSrc, homepageUrl: e.target.value }); setNewSrcErrors((x) => ({ ...x, homepageUrl: undefined })); }} className={inputClass} />
                </Field>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Field label="ADAPTER">
                    <select value={newSrc.adapterKey} onChange={(e) => setNewSrc({ ...newSrc, adapterKey: e.target.value })} className={inputClass}>
                      {ADAPTERS.map((k) => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </Field>
                  <Field label="PUBLISH MODE">
                    <select value={newSrc.publishMode} onChange={(e) => setNewSrc({ ...newSrc, publishMode: e.target.value })} className={inputClass}>
                      <option value="review">review</option>
                      <option value="auto">auto</option>
                    </select>
                  </Field>
                </div>
                <Field label="CONFIG JSON">
                  <textarea placeholder={'Optional — defaults to {"listingUrls":["<url>"]}'} value={newSrc.configJson}
                    onChange={(e) => setNewSrc({ ...newSrc, configJson: e.target.value })} rows={2} className={inputClass} />
                </Field>
                {newSrcErrors.server && <div className="text-[11px] text-[#DC2626]">{newSrcErrors.server}</div>}
                <div className="flex gap-2">
                  <Btn variant="solid" size="card" onClick={handleAddSource}>CREATE</Btn>
                  <Btn size="card" onClick={() => setNewSrc(null)}>CANCEL</Btn>
                </div>
              </div>
            )
          )}
          <div className="overflow-x-auto border-2 border-black bg-white">
            <table className="w-full text-[11px]">
              <thead className="bg-black text-white">
                <tr>
                  {['SOURCE', 'ADAPTER', 'STATUS', 'MODE', 'LIVE', 'QUEUE', 'LAST OK', ''].map((h, i) => (
                    <th key={h || `h${i}`} className="whitespace-nowrap px-3 py-[9px] text-left text-[10px] font-bold uppercase tracking-[0.1em]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sources.length === 0 && (
                  <tr><td colSpan={8}><EmptyLine>NO SOURCES YET.</EmptyLine></td></tr>
                )}
                {sources.map((s, i) => (
                  <tr key={s.id} className={`align-top ${i === 0 ? '' : 'border-t-2 border-[#E5E7EB]'}`}>
                    <td className="px-3 py-2">
                      <div className="font-bold">{s.slug}</div>
                      <div className="text-gray-500">{s.name}</div>
                    </td>
                    <td className="px-3 py-2">{s.adapterKey}</td>
                    <td className="px-3 py-2">
                      {!s.isCrawlable ? (
                        <span className="font-bold text-[#DC2626]">
                          {s.termsStatus === 'prohibited' ? 'PROHIBITED' : !s.robotsAllowed ? 'ROBOTS' : 'OFF'}
                        </span>
                      ) : (
                        <span className="font-bold">OK</span>
                      )}
                      {s.consecutiveFailures > 0 && (
                        <div className="font-bold text-[#DC2626]">{s.consecutiveFailures} fail</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {canPublish ? (
                        <Btn size="row" onClick={() => handlePublishMode(s)} title="review = queue for a human; auto = publish immediately">
                          {String(s.publishMode || '').toUpperCase()}
                        </Btn>
                      ) : (
                        <span className="uppercase">{s.publishMode}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">{s.publishedCount}</td>
                    <td className="px-3 py-2">{s.queuedCount}</td>
                    <td className="whitespace-nowrap px-3 py-2">
                      {s.lastSuccessAt ? new Date(s.lastSuccessAt).toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-[7px]">
                      {(canEdit || canCreate) && (
                        <div className="flex justify-end gap-[6px]">
                          {canEdit && (
                            <>
                              <Btn size="row" onClick={() => handleRun(s, true)} disabled={busyId === s.id}>{busyId === s.id ? '…' : 'DRY'}</Btn>
                              <Btn size="row" onClick={() => handleRun(s, false)} disabled={busyId === s.id}>RUN</Btn>
                              <Btn size="row" onClick={() => handleToggle(s)}>{s.isEnabled ? 'DISABLE' : 'ENABLE'}</Btn>
                            </>
                          )}
                          {canCreate && s.adapterKey === 'manual-csv' && (
                            <Btn size="row" onClick={() => { setCsvTarget(s); setCsvText(''); }}>CSV</Btn>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Moderation queue ── */}
      {view === 'queue' && (
        <div className="border-2 border-black bg-white">
          {queue.length > 0 && (
            <div className="flex items-center justify-between gap-3 border-b-2 border-black px-[14px] py-[11px]">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em]">{queue.length} IN QUEUE</span>
              {canPublish && (
                <Btn variant="solid" size="card" onClick={handleApproveAll} disabled={approvingAll}>
                  {approvingAll ? 'APPROVING…' : 'APPROVE ALL'}
                </Btn>
              )}
            </div>
          )}
          {queue.length === 0 ? (
            <EmptyLine>QUEUE EMPTY.</EmptyLine>
          ) : (
            queue.map((item, i) => {
              let raw = {};
              try { raw = JSON.parse(item.rawPayloadJson || '{}'); } catch { /* show what we can */ }
              const poster = raw.ImageUrl || raw.imageUrl || null;
              return (
                <div key={item.id} className={`flex items-center gap-[14px] px-[14px] py-3 text-xs ${i === 0 ? '' : 'border-t-2 border-[#E5E7EB]'}`}>
                  <div className="h-12 w-12 flex-none overflow-hidden border-2 border-black bg-[#F5F5F5]">
                    {poster && <img src={poster} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[12px] font-bold">{raw.Title || raw.title || '(untitled)'}</div>
                    <div className="text-[11px] text-[#6B7280]">
                      {item.sourceSlug} · {raw.StartsAt || raw.startsAt || 'no date'} ·{' '}
                      {raw.VenueName || raw.venueName || 'no venue'}
                    </div>
                    <a href={item.sourceUrl} target="_blank" rel="noreferrer" className="break-all text-[11px] underline">
                      {item.sourceUrl}
                    </a>
                  </div>
                  {canPublish && (
                    <div className="flex flex-none gap-[6px]">
                      <Btn variant="solid" size="row" onClick={() => handleReview(item, 'approve')}>APPROVE</Btn>
                      <Btn size="row" onClick={() => handleReview(item, 'reject')}>REJECT</Btn>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ── Claims ── */}
      {view === 'claims' && (
        <div className="border-2 border-black bg-white">
          {claims.length === 0 ? (
            <EmptyLine>NO PENDING CLAIMS.</EmptyLine>
          ) : (
            claims.map((c, i) => (
              <div key={c.eventId} className={`flex items-center gap-[14px] px-[14px] py-3 text-xs ${i === 0 ? '' : 'border-t-2 border-[#E5E7EB]'}`}>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[12px] font-bold uppercase tracking-[0.05em]">{c.eventTitle}</div>
                  <div className="text-[11px] text-[#6B7280]">
                    {new Date(c.eventDate).toLocaleDateString()} · requested by{' '}
                    <strong>{c.requesterName}</strong>
                    {c.requesterHandle ? ` (@${c.requesterHandle})` : ''} · {c.requesterEmail}
                  </div>
                </div>
                {(canPublish || canEdit) && (
                  <div className="flex flex-none gap-[6px]">
                    {canPublish && <Btn variant="solid" size="row" onClick={() => handleClaim(c, 'approve')}>APPROVE</Btn>}
                    {canEdit && <Btn size="row" onClick={() => handleClaim(c, 'reject')}>REJECT</Btn>}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* ── CSV staging ── */}
      {canCreate && csvTarget && (
        <div className="flex flex-col gap-3 border-2 border-black bg-white p-[14px]">
          <div className="text-[12px] font-bold uppercase tracking-[0.1em]">STAGE CSV — {csvTarget.slug}</div>
          <div className="text-[11px] text-[#6B7280]">
            Header row required. Recognised columns: external_id, title, start, end, description,
            venue, address, city, price, ticket_url, image_url, tags. Only title and start are required.
          </div>
          <textarea
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            rows={10}
            spellCheck={false}
            className={inputClass}
            placeholder={'title,start,venue,city\nHarvest Dinner,2026-10-01 18:00,The Barn,Penticton'}
          />
          <div className="flex gap-2">
            <Btn variant="solid" size="card" onClick={handleCsvUpload}>STAGE</Btn>
            <Btn size="card" onClick={() => setCsvTarget(null)}>CANCEL</Btn>
          </div>
        </div>
      )}
    </div>
  );
}
